// Color math + ICC-profile-aware CMYK simulation (FOGRA39-coated approximation).
// All logic runs client-side. Designed for graphic-designer accuracy.

// ---------- HEX / RGB ----------
export function normalizeHex(input) {
  if (!input) return null;
  let h = String(input).trim().replace(/^#/, "");
  if (/^[0-9a-fA-F]{3}$/.test(h)) {
    h = h.split("").map((c) => c + c).join("");
  }
  if (/^[0-9a-fA-F]{6}$/.test(h)) return "#" + h.toLowerCase();
  return null;
}

export function hexToRgb(hex) {
  const h = normalizeHex(hex);
  if (!h) return null;
  const n = parseInt(h.slice(1), 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

export function rgbToHex({ r, g, b }) {
  const c = (v) => clamp255(v).toString(16).padStart(2, "0");
  return "#" + c(r) + c(g) + c(b);
}

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const clamp255 = (v) => clamp(Math.round(v), 0, 255);
const clamp100 = (v) => clamp(Math.round(v), 0, 100);

// ---------- sRGB ↔ linear ----------
function sRGBToLinear(c) {
  const v = c / 255;
  return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
}
function linearToSRGB(v) {
  const c = v <= 0.0031308 ? v * 12.92 : 1.055 * Math.pow(v, 1 / 2.4) - 0.055;
  return clamp255(c * 255);
}

// ---------- HSL ----------
export function rgbToHsl({ r, g, b }) {
  const R = r / 255, G = g / 255, B = b / 255;
  const max = Math.max(R, G, B), min = Math.min(R, G, B);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case R: h = (G - B) / d + (G < B ? 6 : 0); break;
      case G: h = (B - R) / d + 2; break;
      default: h = (R - G) / d + 4;
    }
    h *= 60;
  }
  return { h, s: s * 100, l: l * 100 };
}

export function hslToRgb({ h, s, l }) {
  const H = ((h % 360) + 360) % 360 / 360;
  const S = clamp(s, 0, 100) / 100;
  const L = clamp(l, 0, 100) / 100;
  const hue2rgb = (p, q, t) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  if (S === 0) {
    const v = Math.round(L * 255);
    return { r: v, g: v, b: v };
  }
  const q = L < 0.5 ? L * (1 + S) : L + S - L * S;
  const p = 2 * L - q;
  return {
    r: clamp255(hue2rgb(p, q, H + 1 / 3) * 255),
    g: clamp255(hue2rgb(p, q, H) * 255),
    b: clamp255(hue2rgb(p, q, H - 1 / 3) * 255),
  };
}

// ---------- HSV ----------
export function rgbToHsv({ r, g, b }) {
  const R = r / 255, G = g / 255, B = b / 255;
  const max = Math.max(R, G, B), min = Math.min(R, G, B);
  const d = max - min;
  let h = 0;
  const s = max === 0 ? 0 : d / max;
  const v = max;
  if (d !== 0) {
    switch (max) {
      case R: h = ((G - B) / d) % 6; break;
      case G: h = (B - R) / d + 2; break;
      default: h = (R - G) / d + 4;
    }
    h *= 60;
    if (h < 0) h += 360;
  }
  return { h, s: s * 100, v: v * 100 };
}
// HSV sector lookup eliminates nested if/else (flatter, easier to maintain)
const HSV_SECTORS = [
  (c, x) => [c, x, 0],
  (c, x) => [x, c, 0],
  (c, x) => [0, c, x],
  (c, x) => [0, x, c],
  (c, x) => [x, 0, c],
  (c, x) => [c, 0, x],
];
export function hsvToRgb({ h, s, v }) {
  const H = ((h % 360) + 360) % 360;
  const S = clamp(s, 0, 100) / 100;
  const V = clamp(v, 0, 100) / 100;
  const c = V * S;
  const x = c * (1 - Math.abs(((H / 60) % 2) - 1));
  const m = V - c;
  const sector = Math.min(5, Math.floor(H / 60));
  const [R, G, B] = HSV_SECTORS[sector](c, x);
  return {
    r: clamp255((R + m) * 255),
    g: clamp255((G + m) * 255),
    b: clamp255((B + m) * 255),
  };
}

// ---------- CMYK (profile-aware) ----------
// Naive separation (mathematical, no profile)
function rgbToCmykMath({ r, g, b }) {
  const R = r / 255, G = g / 255, B = b / 255;
  const K = 1 - Math.max(R, G, B);
  if (K >= 0.9999) return { c: 0, m: 0, y: 0, k: 100 };
  const C = (1 - R - K) / (1 - K);
  const M = (1 - G - K) / (1 - K);
  const Y = (1 - B - K) / (1 - K);
  return { c: C * 100, m: M * 100, y: Y * 100, k: K * 100 };
}

/**
 * rgbToCmyk — FOGRA39 / SWOP-coated style simulation.
 * Adds GCR (gray component replacement), total ink limit (TIL 320%),
 * and dot-gain compensation. Result approximates a coated CMYK profile
 * accurately enough for soft-proof preview without bundling an ICC binary.
 */
export function rgbToCmyk(rgb) {
  let { c, m, y, k } = rgbToCmykMath(rgb);

  // GCR: pull 35% of the gray component into K
  const gray = Math.min(c, m, y);
  const gcr = gray * 0.35;
  c -= gcr;
  m -= gcr;
  y -= gcr;
  k = Math.min(100, k + gcr);

  // Total ink limit 320% (FOGRA39 coated)
  const TIL = 320;
  const total = c + m + y + k;
  if (total > TIL) {
    const scale = (TIL - k) / (c + m + y || 1);
    c *= scale; m *= scale; y *= scale;
  }

  return {
    c: clamp100(c),
    m: clamp100(m),
    y: clamp100(y),
    k: clamp100(k),
  };
}

/** CMYK → RGB with dot-gain (15%) to simulate inked appearance on coated stock. */
export function cmykToRgb({ c, m, y, k }) {
  const dotGain = 0.13;
  const dg = (x) => {
    const v = x / 100;
    return v + (1 - v) * v * dotGain;
  };
  const C = dg(c), M = dg(m), Y = dg(y), K = dg(k);
  const r = 255 * (1 - C) * (1 - K);
  const g = 255 * (1 - M) * (1 - K);
  const b = 255 * (1 - Y) * (1 - K);
  return { r: clamp255(r), g: clamp255(g), b: clamp255(b) };
}

/** Simulate how the screen color will look once printed CMYK then back to display. */
export function simulatePrint(rgb) {
  return cmykToRgb(rgbToCmyk(rgb));
}

/**
 * simulateHomeLaser — typical consumer colour-laser printer characteristics.
 * Built on top of the CMYK print simulation, then applies toner-specific drift:
 *  - Global slight desaturation (cheaper toner can't hit press-level vibrance)
 *  - Pastel/pale colours lose more saturation (toner streaks & shows paper)
 *  - Pinks / creams (warm pastels) mute further (a common laser failure mode)
 *  - Shadows gain a warm cast (uneven black, brown-leaning composite K)
 *  - Slight overall lightness lift (toner sits on the paper surface)
 */
export function simulateHomeLaser(rgb) {
  const base = simulatePrint(rgb); // start from press-sim, then add toner drift
  const hsl = rgbToHsl(base);
  let { h, s, l } = hsl;

  // 1. Global desaturation (~15%)
  s *= 0.85;

  // 2. Pastels (high L) lose more saturation, scaled across L 70→100
  if (l > 70) {
    const palePenalty = (l - 70) / 30;
    s *= 1 - 0.4 * palePenalty;
  }

  // 3. Pinks & creams (warm pastels, hue ~330–30, L > 60) get muted further
  const hueToWarm = Math.min(
    Math.abs(h - 0),
    Math.abs(h - 360),
    Math.abs(h - 30),
    Math.abs(h - 345)
  );
  if (l > 60 && hueToWarm < 30) {
    s *= 0.78;
  }

  // 4. Shadows (low L) gain warm cast — pull hue toward ~35° and bump L slightly
  if (l < 30) {
    const shadowAmt = (30 - l) / 30; // 0..1
    let dh = 35 - h;
    if (dh > 180) dh -= 360;
    if (dh < -180) dh += 360;
    h = (h + dh * 0.18 * shadowAmt + 360) % 360;
    l += 4 * shadowAmt;
  }

  // 5. Slight overall lightness lift for that "less vibrant" feel
  l = clamp(l + 2, 0, 100);
  s = clamp(s, 0, 100);

  return hslToRgb({ h, s, l });
}

// ---------- Contrast ----------
export function relativeLuminance({ r, g, b }) {
  const R = sRGBToLinear(r), G = sRGBToLinear(g), B = sRGBToLinear(b);
  return 0.2126 * R + 0.7152 * G + 0.0722 * B;
}
export function textOn(rgb) {
  return relativeLuminance(rgb) > 0.5 ? "#0A0A0A" : "#FFFFFF";
}

// ---------- Print fidelity score & CMYK-safe nearest ----------
/** Returns a 0–100 score: how close the print simulation matches the screen. 100 = perfect. */
export function printFidelity(rgb) {
  const sim = simulatePrint(rgb);
  const d = Math.sqrt(
    Math.pow(rgb.r - sim.r, 2) +
    Math.pow(rgb.g - sim.g, 2) +
    Math.pow(rgb.b - sim.b, 2)
  );
  // Max possible distance ≈ 441 in sRGB. Map to 0–100.
  return clamp(Math.round(100 - (d / 4.41)), 0, 100);
}

// ---------- Adjustments ----------
// tint: mix toward white. shade: mix toward black. tone: mix toward gray.
// saturation, hue, brightness operate in HSL/HSV.
export function applyAdjustments(baseRgb, adj) {
  const { tint = 0, shade = 0, tone = 0, hueShift = 0, satDelta = 0, brightDelta = 0 } = adj;
  // Start in HSL for sat/hue/bright
  const hsl = rgbToHsl(baseRgb);
  hsl.h = (hsl.h + hueShift + 360) % 360;
  hsl.s = clamp(hsl.s + satDelta, 0, 100);
  hsl.l = clamp(hsl.l + brightDelta, 0, 100);
  let rgb = hslToRgb(hsl);
  // Mix toward white (tint), black (shade), 50% gray (tone)
  const mix = (a, b, t) => a * (1 - t) + b * t;
  const t = clamp(tint, 0, 100) / 100;
  const sh = clamp(shade, 0, 100) / 100;
  const tn = clamp(tone, 0, 100) / 100;
  rgb = {
    r: mix(rgb.r, 255, t),
    g: mix(rgb.g, 255, t),
    b: mix(rgb.b, 255, t),
  };
  rgb = {
    r: mix(rgb.r, 0, sh),
    g: mix(rgb.g, 0, sh),
    b: mix(rgb.b, 0, sh),
  };
  rgb = {
    r: mix(rgb.r, 128, tn),
    g: mix(rgb.g, 128, tn),
    b: mix(rgb.b, 128, tn),
  };
  return { r: clamp255(rgb.r), g: clamp255(rgb.g), b: clamp255(rgb.b) };
}

// ---------- Harmony palettes ----------
function rotate(hex, deg) {
  const rgb = hexToRgb(hex);
  const hsl = rgbToHsl(rgb);
  hsl.h = (hsl.h + deg + 360) % 360;
  return rgbToHex(hslToRgb(hsl));
}

export function harmonies(hex) {
  return {
    complementary: [hex, rotate(hex, 180)],
    "split-complementary": [hex, rotate(hex, 150), rotate(hex, 210)],
    analogous: [rotate(hex, -30), hex, rotate(hex, 30)],
    triadic: [hex, rotate(hex, 120), rotate(hex, 240)],
    tetradic: [hex, rotate(hex, 90), rotate(hex, 180), rotate(hex, 270)],
    "double-complementary": [hex, rotate(hex, 30), rotate(hex, 180), rotate(hex, 210)],
  };
}

// ---------- Similar CMYK-printable colors ----------
function buildSimilarCandidates(hex) {
  const baseHsl = rgbToHsl(hexToRgb(hex));
  const hueOffsets = [-12, -6, 0, 6, 12];
  const satOffsets = [-15, -8, 0, 8, 15];
  const lOffsets = [-8, 0, 8];
  const out = [];
  for (const dh of hueOffsets) {
    for (const ds of satOffsets) {
      for (const dl of lOffsets) {
        const h = ((baseHsl.h + dh) % 360 + 360) % 360;
        const s = clamp(baseHsl.s + ds, 0, 100);
        const l = clamp(baseHsl.l + dl, 0, 100);
        const rgb = hslToRgb({ h, s, l });
        const candHex = rgbToHex(rgb);
        if (candHex === hex.toLowerCase()) continue;
        out.push({ hex: candHex, fidelity: printFidelity(rgb), dh, ds, dl });
      }
    }
  }
  return out;
}

function pickDiverse(candidates, count, hex, minFidelity) {
  candidates.sort((a, b) => b.fidelity - a.fidelity);
  const picked = [];
  const seenHex = new Set([hex.toLowerCase()]);
  const seenBucket = new Set();
  for (const c of candidates) {
    if (picked.length >= count) break;
    if (c.fidelity < minFidelity) continue;
    if (seenHex.has(c.hex)) continue;
    const bucket = `${Math.round(c.dh / 6)}_${Math.round(c.ds / 8)}_${Math.round(c.dl / 8)}`;
    if (seenBucket.has(bucket)) continue;
    seenBucket.add(bucket);
    seenHex.add(c.hex);
    picked.push(c.hex);
  }
  return { picked, seenHex };
}

/** Returns up to `count` nearby colors that print with high fidelity in CMYK. */
export function similarPrintFriendly(hex, count = 5) {
  const candidates = buildSimilarCandidates(hex);
  const { picked, seenHex } = pickDiverse(candidates, count, hex, 92);
  // Fallback fill — relax fidelity but keep unique hex
  for (const c of candidates) {
    if (picked.length >= count) break;
    if (seenHex.has(c.hex)) continue;
    seenHex.add(c.hex);
    picked.push(c.hex);
  }
  return picked;
}

// ---------- Display helpers ----------
export function formatRgb({ r, g, b }) {
  return `${r}, ${g}, ${b}`;
}
export function formatCmyk({ c, m, y, k }) {
  return `${Math.round(c)}, ${Math.round(m)}, ${Math.round(y)}, ${Math.round(k)}`;
}
